import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import AddressAutocomplete from './GooglePlacesProxyInput';
import { 
  Save, 
  X, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  DollarSign, 
  Plus,
  Trash2,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DetailedJobForm = ({ job, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    detailed_location: '',
    location_name: '',
    gps_coordinates: { lat: '', lng: '' },
    client_name: '',
    client_contact_name: '',
    client_contact_phone: '',
    client_contact_email: '',
    pricing_breakdown: [{ item: '', quantity: 1, price: 0 }],
    brand_requirements: '',
    alternate_brand_offer: '',
    special_notes: ''
  });

  useEffect(() => {
    // Prefill with existing data if available
    if (job) {
      setFormData({
        detailed_location: job.detailed_location || '',
        location_name: job.location_name || '',
        gps_coordinates: job.gps_coordinates || { lat: '', lng: '' },
        client_name: job.client_name || '',
        client_contact_name: job.client_contact_name || '',
        client_contact_phone: job.client_contact_phone || '',
        client_contact_email: job.client_contact_email || '',
        pricing_breakdown: job.pricing_breakdown || [{ item: '', quantity: 1, price: 0 }],
        brand_requirements: job.brand_requirements || '',
        alternate_brand_offer: job.alternate_brand_offer || '',
        special_notes: job.special_notes || ''
      });
    }
  }, [job]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGpsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      gps_coordinates: {
        ...prev.gps_coordinates,
        [field]: value
      }
    }));
  };

  const handlePricingChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      pricing_breakdown: prev.pricing_breakdown.map((item, i) => 
        i === index ? { ...item, [field]: field === 'price' || field === 'quantity' ? parseFloat(value) || 0 : value } : item
      )
    }));
  };

  const addPricingItem = () => {
    setFormData(prev => ({
      ...prev,
      pricing_breakdown: [...prev.pricing_breakdown, { item: '', quantity: 1, price: 0 }]
    }));
  };

  const removePricingItem = (index) => {
    setFormData(prev => ({
      ...prev,
      pricing_breakdown: prev.pricing_breakdown.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean up pricing breakdown
      const cleanPricingBreakdown = formData.pricing_breakdown.filter(item => 
        item.item.trim() !== '' || item.quantity > 0 || item.price > 0
      );

      const submitData = {
        ...formData,
        pricing_breakdown: cleanPricingBreakdown,
        gps_coordinates: formData.gps_coordinates.lat && formData.gps_coordinates.lng 
          ? formData.gps_coordinates 
          : null
      };

      await axios.put(`${API}/jobs/${job.id}/details`, submitData);
      toast.success('Job details updated successfully!');
      
      onSuccess();
      
    } catch (error) {
      console.error('Failed to update job details:', error);
      toast.error('Failed to update job details');
    } finally {
      setLoading(false);
    }
  };

  const totalPrice = formData.pricing_breakdown.reduce((sum, item) => 
    sum + (item.quantity * item.price), 0
  );

  return (
    <div className="jop-card p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Add Detailed Information</h2>
          <p className="text-sm text-gray-600 mt-1">
            Help receivers deliver the perfect service (visible only to assigned receiver)
          </p>
          
          {/* Quick Post Summary */}
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-sm font-medium text-green-800 mb-2">✅ Job Posted Successfully:</h3>
            <div className="text-xs text-green-700 space-y-1">
              <div><strong>{job.title}</strong> - {job.industry.join(', ')}</div>
              <div>{job.location} • ${job.quoted_price} • {job.urgency_level.toUpperCase()}</div>
              <div>{job.job_specification}</div>
            </div>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Location Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-gray-400" />
            Precise Location
          </h3>
          
          <AddressAutocomplete
            value={formData.detailed_location}
            onChange={(value) => handleChange({ target: { name: 'detailed_location', value } })}
            onLocationNameChange={(name) => handleChange({ target: { name: 'location_name', value: name } })}
            onCoordinatesChange={(coords) => {
              setFormData(prev => ({
                ...prev,
                gps_coordinates: coords
              }));
            }}
            placeholder="123 Main Street, Newcastle NSW 2300"
            label="Full Address"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location Name (Business/Landmark)
            </label>
            <input
              type="text"
              name="location_name"
              value={formData.location_name}
              onChange={handleChange}
              className="jop-input"
              placeholder="e.g., Newcastle Tyre Service Center"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              GPS Coordinates (Latitude, Longitude)
            </label>
            <input
              type="text"
              value={`${formData.gps_coordinates.lat || ''}${formData.gps_coordinates.lat && formData.gps_coordinates.lng ? ', ' : ''}${formData.gps_coordinates.lng || ''}`}
              onChange={(e) => {
                const coords = e.target.value.split(',').map(s => s.trim());
                const lat = coords[0] || '';
                const lng = coords[1] || '';
                setFormData(prev => ({
                  ...prev,
                  gps_coordinates: { lat, lng }
                }));
              }}
              className="jop-input"
              placeholder="-32.9267, 151.7789"
            />
          </div>
        </div>

        {/* Client Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <User className="h-5 w-5 mr-2 text-gray-400" />
            Client Information
          </h3>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center">
              <AlertCircle className="h-4 w-4 text-blue-600 mr-2" />
              <span className="text-xs text-blue-700">
                Client details remain private until job is accepted
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client/Company Name
              </label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                className="jop-input"
                placeholder="ABC Transport"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person
              </label>
              <input
                type="text"
                name="client_contact_name"
                value={formData.client_contact_name}
                onChange={handleChange}
                className="jop-input"
                placeholder="John Smith"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Phone
              </label>
              <input
                type="tel"
                name="client_contact_phone"
                value={formData.client_contact_phone}
                onChange={handleChange}
                className="jop-input"
                placeholder="+61 400 000 000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email
              </label>
              <input
                type="email"
                name="client_contact_email"
                value={formData.client_contact_email}
                onChange={handleChange}
                className="jop-input"
                placeholder="contact@client.com"
              />
            </div>
          </div>
        </div>

        {/* Pricing Breakdown */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-gray-400" />
            Pricing Breakdown
          </h3>

          <div className="space-y-3">
            {formData.pricing_breakdown.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item/Service
                  </label>
                  <input
                    type="text"
                    value={item.item}
                    onChange={(e) => handlePricingChange(index, 'item', e.target.value)}
                    className="jop-input"
                    placeholder="e.g., Tyre 235/75R15"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Qty
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handlePricingChange(index, 'quantity', e.target.value)}
                    className="jop-input"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Price ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.price}
                    onChange={(e) => handlePricingChange(index, 'price', e.target.value)}
                    className="jop-input"
                  />
                </div>
                <div className="col-span-2 flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">
                    ${(item.quantity * item.price).toFixed(2)}
                  </span>
                  {formData.pricing_breakdown.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePricingItem(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={addPricingItem}
              className="flex items-center text-sm text-amber-600 hover:text-amber-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </button>
            <div className="text-lg font-semibold text-gray-900">
              Total: ${totalPrice.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Brand & Special Requirements */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand Specific Requirements
            </label>
            <textarea
              name="brand_requirements"
              value={formData.brand_requirements}
              onChange={handleChange}
              rows="3"
              className="jop-input resize-none"
              placeholder="e.g., Must use Michelin or equivalent, Continental preferred"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alternate Brand Offer
            </label>
            <textarea
              name="alternate_brand_offer"
              value={formData.alternate_brand_offer}
              onChange={handleChange}
              rows="3"
              className="jop-input resize-none"
              placeholder="e.g., Can offer Bridgestone at $50 discount"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Notes & Instructions
          </label>
          <textarea
            name="special_notes"
            value={formData.special_notes}
            onChange={handleChange}
            rows="4"
            className="jop-input resize-none"
            placeholder="Any special requirements, access instructions, timing constraints, etc."
          />
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={loading}
            className="jop-button flex-1 flex items-center justify-center"
          >
            {loading ? (
              <div className="spinner" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Details
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="jop-button-secondary"
          >
            Skip for Now
          </button>
        </div>
      </form>
    </div>
  );
};

export default DetailedJobForm;